import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	MouseSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * AutoCompleteByID component with optional drag and drop functionality
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - Field name for form handling
 * @param {any} props.value - Current value(s)
 * @param {Array} props.options - Available options array
 * @param {string} props.optionLabel - Property name to use for option labels
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder text
 * @param {Function} props.onChange - Change handler function
 * @param {Function} props.getOptionLabel - Custom function to get option labels
 * @param {string} props.helperText - Helper text below input
 * @param {boolean} props.error - Error state
 * @param {boolean} props.required - Required field
 * @param {boolean} props.multiple - Enable multiple selection
 * @param {boolean} props.draggable - Enable drag and drop (only has effect when multiple=true)
 */
const AutoCompleteByID = ({
	name,
	value,
	options,
	optionLabel,
	label,
	placeholder,
	onChange,
	getOptionLabel,
	helperText,
	error,
	required,
	multiple,
	draggable = false,
	lockedValueIds,
	...props
}) => {
	const opts = (options || []);
	const lockedIds = Array.isArray(lockedValueIds) ? lockedValueIds : [];

	// Helper function to format option labels consistently
	const formatOptionLabel = (optionId) => {
		const option = opts.find(u => u.id === optionId);
		if (props.freeSolo && !option && getOptionLabel) return getOptionLabel(optionId);
		if (!option) return '-';
		if (optionLabel && option[optionLabel]) return option[optionLabel];
		if (getOptionLabel && getOptionLabel(option)) return getOptionLabel(option);
		return option.label || '-';
	};

	// Sortable item component for drag and drop
	const SortableItem = (itemProps) => {
		const { attributes, listeners, setNodeRef, transform, transition } =
			useSortable({ id: itemProps.value });

		const style = {
			transform: CSS.Transform.toString(transform),
			transition,
		};

		return (
			<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
				<Chip
					key={itemProps.value}
					variant="outlined"
					label={formatOptionLabel(itemProps.value)}
					onDelete={itemProps.onDelete}
					sx={{
						cursor: 'grab',
						'&:active': {
							cursor: 'grabbing',
						},
					}}
				/>
			</div>
		);
	};

	// Sortable chips wrapper component
	const SortableChips = ({ tagValues, getTagProps }) => {
		const sensors = useSensors(
			useSensor(MouseSensor, { activationConstraint: { distance: 20 } }),
			useSensor(KeyboardSensor, {
				coordinateGetter: sortableKeyboardCoordinates,
			}),
		);

		const handleDragEnd = (event) => {
			const { active, over } = event;

			if (active && over && active.id !== over.id) {
				const oldIndex = tagValues.indexOf(active.id);
				const newIndex = tagValues.indexOf(over.id);
				
				if (oldIndex !== -1 && newIndex !== -1) {
					const newValue = arrayMove(tagValues, oldIndex, newIndex);
					onChange({ target: { name, value: newValue } }, newValue);
				}
			}
		};

		return (
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext items={tagValues} strategy={verticalListSortingStrategy}>
					{tagValues.map((option, index) => {
						const tagProps = getTagProps({ index });
						const { key, onDelete, ...otherProps } = tagProps;
						return (
							<SortableItem
								key={key}
								value={option}
								onDelete={onDelete}
								{...otherProps}
							/>
						);
					})}
				</SortableContext>
			</DndContext>
		);
	};

	const renderTags = multiple && draggable
		? (tagValues, getTagProps) => <SortableChips tagValues={tagValues} getTagProps={getTagProps} />
		: multiple && lockedIds.length
			? (tagValues, getTagProps) =>
				tagValues.map((option, index) => {
					const tagProps = getTagProps({ index });
					const { key, onDelete, ...chipProps } = tagProps;
					const locked = lockedIds.includes(option);
					return (
						<Chip
							key={key}
							variant="outlined"
							label={formatOptionLabel(option)}
							{...(locked ? {} : { onDelete })}
							{...chipProps}
						/>
					);
				})
			: undefined;

	return <Autocomplete
		clearOnBlur={false}
		openOnFocus
		value={value || (multiple ? [] : null)}
		options={(opts || []).map(o => o.id)}
		renderInput={(params) => <TextField {...params} helperText={helperText} error={error} sx={{ marginBottom: 2 }} label={label} placeholder={placeholder} required={required} />}
		renderTags={renderTags}
		onChange={(e, v) => {
			let nextValue = multiple
				? v.map((u) => (typeof u === 'string' ? u : u?.id))
				: (typeof v === 'string' ? v : v?.id);
			if (multiple && lockedIds.length) {
				const set = new Set(nextValue || []);
				lockedIds.forEach((id) => set.add(id));
				nextValue = Array.from(set);
			}
			return onChange({ target: { name, value: nextValue } }, nextValue);
		}}
		getOptionLabel={formatOptionLabel}
		multiple={multiple}
		filterSelectedOptions
		{...props}
	/>;
};

AutoCompleteByID.type = 'AutoCompleteByID';

export default AutoCompleteByID;
